import { ActionIcon, Alert, Button, Center, Collapse, Group, Paper, Stack, Text, useMantineTheme } from '@mantine/core';
import { Dropzone, FileRejection, FileWithPath } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { IconCloudUpload, IconInfoCircle, IconTrash, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import CheckToChat from '@/components/Create_V2/CheckToChat';
import { SvgFileDragAdd } from '@/components/Create_V2/components/Documents/icon';
import { useTaskFAQ } from '@/components/Create_V2/components/FAQsList/useTaskFAQ';
import TaskList from '@/components/Create_V2/components/TaskList';
import Description from '@/components/Create_V2/Description';
import { ModelSelect } from '@/components/Create_V2/ModelSelect';
import FileIcon from '@/components/FileIcon';
import { usePaymentInfo } from '@/hooks/usePaymentInfo';
import { useCreateStore } from '@/store/create';
import { formatFileSize } from '@/utils/helper';
import { queuePromises } from '@/utils/task';

export default () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { creating, pageType, setDatasetId, toggle } = useCreateStore();
  const { fileLimitSize } = usePaymentInfo();
  const { id } = useParams();

  const TotalStepsOri = [
    t('create_v2.faqs_list.step_1'),
    t('create_v2.faqs_list.step_2'),
    t('create_v2.faqs_list.step_3'),
  ];

  const [file, setFile] = useState<File | null>(null);

  const { taskProcess, tasks } = useTaskFAQ();
  const [totalSteps, setTotalSteps] = useState(TotalStepsOri);
  const [step, setStep] = useState(0);
  const [failStep, _setFailStep] = useState(0);
  const hasFailStepRef = useRef(false);
  const setFailStep = (index: number) => {
    setStep(index);
    _setFailStep(index);
    if (index) hasFailStepRef.current = true;
  };

  const acceptedFiles = (acceptedFiles: FileWithPath[]) => {
    setFile(acceptedFiles[0]);
  };
  const rejectedFiles = (fileRejections: FileRejection[]) => {
    const { errors, file } = fileRejections[0];
    const fileName = file.name;
    const fileError = errors.map((error) => {
      const i18nKey = `errors.dropzone.${error.code}`;
      const translated = t(i18nKey);
      // 如果翻译key不存在，i18next会返回key本身，此时使用原始message
      return translated === i18nKey ? error.message : translated;
    });
    showNotification({
      message: (
        <>
          <Text>{fileName}</Text>
          <Text>{fileError}</Text>
        </>
      ),
      color: 'red',
    });
  };

  const handleFile = async () => {
    if (!file) return;
    toggle();

    const firstParam = file;
    const successCallback = (index: number) => {
      setTotalSteps(TotalStepsOri);
      setStep(index);
    };
    const failedCallback = (index: number, error: string) => {
      setFailStep(index);
      setTotalSteps((arr) => [...arr.slice(0, index - 1), error, ...arr.slice(index)]);
    };
    queuePromises(firstParam, tasks, successCallback, failedCallback);
  };

  const deleteFile = () => {
    setFile(null);
  };

  const retryTask = () => {
    pageType === 'create' && setDatasetId('');
    setStep(0);
    setFailStep(0);
    toggle();
    setTotalSteps(TotalStepsOri);
  };

  useEffect(() => {
    if (hasFailStepRef.current) return;
    if (taskProcess) {
      setTotalSteps((arr) => [...arr.slice(0, 1), TotalStepsOri[1] + `  ${taskProcess}%`, ...arr.slice(2)]);
    }
  }, [taskProcess]);

  const FileCard = () => (
    <Collapse in={!!file}>
      <Paper shadow="md" radius="lg">
        {file && (
          <ul className="p-5 max-h-[50vh] overflow-auto">
            <li key={file.name + file.lastModified} className="group p-2 rounded-md hover:bg-gray-50">
              <Group justify="space-between" wrap="nowrap">
                <Group wrap="nowrap">
                  <FileIcon fileName={file.name} />
                  <Text size={'lg'}>{file.name}</Text>
                </Group>
                <section>
                  <Text className="h-[28px] group-hover:hidden">{formatFileSize(file.size)}</Text>
                  <ActionIcon className={'hidden group-hover:flex'} color="red" onClick={deleteFile}>
                    <IconTrash size={'1rem'} />
                  </ActionIcon>
                </section>
              </Group>
            </li>
          </ul>
        )}
      </Paper>
    </Collapse>
  );

  const APIBaseURL = window.ENV.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;

  const downloadUrl = id
    ? `${APIBaseURL}/datasets/${id}/faqs/download-faq-template`
    : `${APIBaseURL}/datasets/faqs/download`;

  return (
    <section>
      <Stack>
        <Group justify="space-between">
          <h3 className="gray.8 font-medium">{t('create_v2.faqs_list.label')}</h3>
          {!creating && !file && (
            <div>
              <a href={downloadUrl} className="underline text-sm text-gray-500">
                <Trans
                  i18nKey="faqs.import.sheet.download_template"
                  components={{
                    span: <span className="text-violet-800" />,
                  }}
                />
              </a>
            </div>
          )}
        </Group>

        <Collapse in={!creating}>
          <Collapse in={!file}>
            <section className="bg-white p-6 rounded-2xl shadow-lg">
              <Dropzone
                className="border-none bg-gray-50 rounded-xl hover:bg-gray-100"
                onDrop={acceptedFiles}
                onReject={rejectedFiles}
                loading={false}
                maxSize={fileLimitSize * 1024 * 1024}
                multiple={false}
                accept={['.doc', '.docx', '.xls', '.xlsx', '.pdf', '.txt', '.html', '.md']}
              >
                <Stack className="flex items-center justify-center min-h-[180px] pointer-events-none" gap="xl">
                  <Dropzone.Accept>
                    <IconCloudUpload size="3.2rem" stroke={1.5} color={theme.colors[theme.primaryColor][6]} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX size="3.2rem" stroke={1.5} color={theme.colors.red[6]} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <SvgFileDragAdd />
                  </Dropzone.Idle>

                  <div className="text-center">
                    <Text color="indigo" size="xl" fw={500} inline>
                      {t('faqs.import.sheet.choose_file')}
                    </Text>
                    <Text size="sm" c="gray.5" inline mt={10}>
                      {t('faqs.import.sheet.description')}
                    </Text>
                  </div>
                </Stack>
              </Dropzone>
            </section>
            <section className="relative mt-5">
              <Description
                text={t('create_v2.faqs_list.description.text')}
                hoverText={t('create_v2.faqs_list.description.detail')}
              />
            </section>
          </Collapse>

          <Collapse in={!!file}>
            <FileCard />
          </Collapse>

          <ModelSelect />

          <Center className="mt-10">
            <Button size="md" onClick={handleFile} disabled={!file}>
              {t('faqs.import.sheet.upload_file')}
            </Button>
          </Center>
        </Collapse>

        <Collapse in={creating} mt={10}>
          <FileCard />

          <section className="mt-10">
            <TaskList step={step} totalSteps={totalSteps} failStep={failStep} />
          </section>

          {step === TotalStepsOri.length && !failStep && (
            <Alert variant="light" color="violet" icon={<IconInfoCircle />} className="mt-5">
              <Text size="sm">{t('faqs.import.filter.learning_in_progress')}</Text>
            </Alert>
          )}

          <Center className="mt-10">
            {failStep ? (
              <Button size="md" onClick={retryTask}>
                {t('errors.initialize.button')}
              </Button>
            ) : (
              <CheckToChat disabled={step !== TotalStepsOri.length} type="faq" />
            )}
          </Center>
        </Collapse>
      </Stack>
    </section>
  );
};
